import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Check, X, Package, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InventorySearchPicker
 * ---------------------
 * Prominent search field that hits `/api/inventory/search?q=…&available_only=true`
 * and shows a table of available inventory. Clicking Select fires `onSelect(item)`
 * and (optionally) closes the picker.
 *
 * Props:
 *  - selectedIds: string[]  — already-picked inventory ids (rendered as chips)
 *  - onSelect(item)         — called when user picks a row
 *  - onRemove(id)           — remove a previously-picked item
 *  - autoFocus              — focus the search input on mount
 *  - multi                  — when true, does NOT clear results after picking
 *                             so the user can keep selecting more products
 */
export default function InventorySearchPicker({
  selectedIds = [], onSelect, onRemove, autoFocus = true, multi = false,
}) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickedItems, setPickedItems] = useState({}); // id → item (for chip display)
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setRows([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API_URL}/api/inventory/search?q=${encodeURIComponent(q.trim())}&available_only=true&limit=50`,
          { withCredentials: true },
        );
        setRows(data || []);
      } catch (e) {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q]);

  const identifierOf = (i) => (
    i.imei || i.imei2 || i.serial_number || i.engine_number || i.chassis_number ||
    i.vin || i.registration_number || i.property_registration_number || '—'
  );

  const displayName = (i) => {
    const parts = [i.brand, i.model].filter(Boolean).join(' ');
    return parts || i.product_type || 'Product';
  };

  const isAvailable = (i) =>
    (i.is_sold !== true) && (i.stock_status || 'Available') !== 'Sold';

  const handleSelect = (item) => {
    if (selectedIds.includes(item.id)) return;
    setPickedItems((s) => ({ ...s, [item.id]: item }));
    onSelect && onSelect(item);
    if (!multi) {
      setQ('');
      setRows([]);
    }
  };

  const chips = useMemo(() => {
    return selectedIds.map((id) => pickedItems[id] || { id });
  }, [selectedIds, pickedItems]);

  return (
    <div className="space-y-2" data-testid="inventory-search-picker">
      {/* Chips of picked items */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="inv-picker-chips">
          {chips.map((it) => (
            <div key={it.id} className="flex items-center bg-emerald-900/40 border border-emerald-600/50 rounded-sm px-2 py-1 text-xs text-emerald-200"
              data-testid={`inv-chip-${it.id}`}>
              <Package className="h-3 w-3 mr-1" />
              <span className="mr-1">{it.brand || it.product_type || 'Item'} {it.model || ''}</span>
              {it.product_id && <span className="text-emerald-400/70 mr-1">· {it.product_id}</span>}
              {it.sale_price && <span className="text-emerald-300 mr-1">· PKR {it.sale_price.toLocaleString()}</span>}
              {onRemove && (
                <button type="button" onClick={() => onRemove(it.id)} className="ml-1 hover:text-white"
                  data-testid={`inv-chip-remove-${it.id}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Prominent search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-500" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search inventory by Serial No / IMEI / IMEI2 / Product ID / Model / Barcode…"
          className="pl-9 bg-slate-800 border-amber-500/40 text-white placeholder:text-slate-500 focus-visible:ring-amber-500"
          data-testid="inventory-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 animate-spin" />
        )}
      </div>

      {/* Results table */}
      {q.trim() !== '' && (
        <div className="border border-slate-700 rounded-sm max-h-72 overflow-auto bg-slate-950/60"
          data-testid="inv-search-results">
          {rows.length === 0 && !loading && (
            <div className="text-slate-500 text-xs text-center py-4" data-testid="inv-search-empty">
              No available inventory matches "{q}"
            </div>
          )}
          {rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800 sticky top-0 bg-slate-900">
                  <TableHead className="text-slate-400 text-xs">Product</TableHead>
                  <TableHead className="text-slate-400 text-xs">Model</TableHead>
                  <TableHead className="text-slate-400 text-xs">Product ID</TableHead>
                  <TableHead className="text-slate-400 text-xs">Serial / IMEI</TableHead>
                  <TableHead className="text-slate-400 text-xs text-right">Price</TableHead>
                  <TableHead className="text-slate-400 text-xs">Status</TableHead>
                  <TableHead className="text-slate-400 text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((i) => {
                  const already = selectedIds.includes(i.id);
                  const avail = isAvailable(i);
                  return (
                    <TableRow key={i.id} className="border-slate-800 hover:bg-slate-800/60"
                      data-testid={`inv-search-row-${i.id}`}>
                      <TableCell className="text-white text-xs">{displayName(i)}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{i.model || '—'}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{i.product_id || '—'}</TableCell>
                      <TableCell className="text-slate-300 text-xs font-mono">{identifierOf(i)}</TableCell>
                      <TableCell className="text-emerald-400 text-xs text-right">PKR {(i.sale_price || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-[10px] uppercase rounded-sm ${avail
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-red-500/20 text-red-400'}`}>
                          {avail ? 'Available' : 'Sold'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm" type="button"
                          disabled={!avail || already}
                          onClick={() => handleSelect(i)}
                          data-testid={`inv-select-${i.id}`}
                          className={`h-7 px-2 text-[11px] ${already
                            ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                            : 'bg-amber-500 hover:bg-amber-600 text-slate-950'}`}
                        >
                          {already ? <><Check className="h-3 w-3 mr-1" /> Selected</> : 'Select'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );
}
